import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

const publicClient = axios.create({ baseURL: process.env.REACT_APP_API_BASE_URL });

const palette = {
  teal: '#0784a7',
  tealDark: '#055f7a',
  bannerDark: '#15303d',
  ink: '#1e272e',
  inkSoft: '#4a5560',
  muted: '#7a8590',
  cardBg: '#fff',
  pageBg: '#f5f7fb',
  hair: '#e6e9ec',
};

const pageStyles = {
  shell: {
    minHeight: '100vh',
    background: palette.pageBg,
    fontFamily: 'Montserrat, Inter, system-ui, sans-serif',
    color: palette.ink,
    padding: '40px 16px 64px',
    boxSizing: 'border-box',
  },
  container: {
    maxWidth: 720,
    margin: '0 auto',
  },
  // Banner mirrors backend/libs/emailTemplates/meetingReportShared.js — same
  // navy band + icon + MULTIPLY wordmark — so recipients see continuity from
  // the email they clicked into the page that loads.
  banner: {
    background: palette.bannerDark,
    borderRadius: 14,
    padding: '18px 24px',
    marginBottom: 16,
    display: 'flex',
    alignItems: 'center',
    boxShadow: '0 8px 24px rgba(15, 30, 50, 0.06)',
  },
  bannerIcon: {
    width: 26,
    height: 21,
    marginRight: 12,
  },
  bannerWordmark: {
    fontFamily: 'Montserrat, Inter, sans-serif',
    fontWeight: 700,
    fontSize: 18,
    letterSpacing: 0.4,
    color: '#ffffff',
  },
  card: {
    background: palette.cardBg,
    borderRadius: 14,
    padding: '32px 36px',
    boxShadow: '0 8px 24px rgba(15, 30, 50, 0.06)',
  },
  h1: {
    margin: '8px 0 4px',
    fontSize: 22,
    fontWeight: 700,
    color: palette.ink,
    lineHeight: 1.25,
  },
  metaRow: {
    fontSize: 13,
    color: palette.muted,
    marginBottom: 24,
  },
  sectionTitle: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 0.9,
    textTransform: 'uppercase',
    color: palette.teal,
    marginTop: 24,
    marginBottom: 10,
  },
  summary: {
    fontSize: 14,
    lineHeight: 1.65,
    color: palette.inkSoft,
    whiteSpace: 'pre-wrap',
  },
  nextStepsList: {
    listStyle: 'none',
    padding: 0,
    margin: 0,
  },
  nextStepItem: {
    background: '#f6f8fa',
    borderRadius: 8,
    padding: '12px 14px',
    marginBottom: 10,
    fontSize: 13,
    lineHeight: 1.55,
    color: palette.ink,
  },
  ctaWrap: {
    marginTop: 32,
    paddingTop: 24,
    borderTop: `1px solid ${palette.hair}`,
    textAlign: 'center',
  },
  ctaLead: {
    color: palette.inkSoft,
    fontSize: 14,
    marginBottom: 16,
  },
  ctaButton: {
    display: 'inline-block',
    background: palette.teal,
    color: '#fff',
    textDecoration: 'none',
    fontWeight: 600,
    padding: '12px 24px',
    borderRadius: 999,
    fontSize: 13,
    letterSpacing: 0.3,
  },
  footer: {
    textAlign: 'center',
    marginTop: 32,
    fontSize: 12,
    color: palette.muted,
  },
  state: {
    background: palette.cardBg,
    borderRadius: 14,
    padding: '40px 32px',
    boxShadow: '0 8px 24px rgba(15, 30, 50, 0.06)',
    textAlign: 'center',
    color: palette.ink,
  },
};

const BrandHeader = () => (
  <div style={pageStyles.banner}>
    <img src="/images/multiply-icon.png" alt="" style={pageStyles.bannerIcon} />
    <span style={pageStyles.bannerWordmark}>MULTIPLY</span>
  </div>
);

const formatMeetingDate = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
};

const PublicSharedReportPage = () => {
  const { shareToken } = useParams();
  const [state, setState] = useState({ status: 'loading', data: null, error: null });

  useEffect(() => {
    let cancelled = false;
    const fetchReport = async () => {
      try {
        const response = await publicClient.get(`/public/share/${shareToken}`);
        if (!cancelled) {
          setState({ status: 'ready', data: response.data, error: null });
        }
      } catch (err) {
        if (cancelled) return;
        const status = err?.response?.status;
        if (status === 410) {
          setState({ status: 'expired', data: null, error: null });
        } else if (status === 404) {
          setState({ status: 'not-found', data: null, error: null });
        } else {
          console.error('PublicSharedReportPage: fetch failed', err);
          setState({ status: 'error', data: null, error: err?.message || 'Unknown error' });
        }
      }
    };
    if (shareToken) {
      fetchReport();
    }
    return () => { cancelled = true; };
  }, [shareToken]);

  if (state.status === 'loading') {
    return (
      <div style={pageStyles.shell}>
        <div style={pageStyles.container}>
          <BrandHeader />
          <div style={pageStyles.state}>Loading meeting summary…</div>
        </div>
      </div>
    );
  }

  if (state.status === 'expired') {
    return renderState(
      'This report is no longer accessible',
      'The shared meeting summary has expired and is no longer available. If you still need it, ask the salesperson to send you a fresh link.',
    );
  }

  if (state.status === 'not-found') {
    return renderState(
      'Summary not available',
      'We couldn\'t find that meeting summary. The link may have been mistyped.',
    );
  }

  if (state.status === 'error') {
    return renderState(
      'Something went wrong',
      'We couldn\'t load the meeting summary right now. Please try again in a moment.',
    );
  }

  const { meetingTitle, meetingDate, summary, keyPoints, nextSteps, shareTokenExpiresAt } = state.data || {};
  const metaParts = [];
  if (meetingDate) metaParts.push(formatMeetingDate(meetingDate));
  const expiresLabel = (() => {
    if (!shareTokenExpiresAt) return null;
    try {
      const d = new Date(shareTokenExpiresAt);
      if (isNaN(d.getTime())) return null;
      return d.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
    } catch {
      return null;
    }
  })();

  return (
    <div style={pageStyles.shell}>
      <div style={pageStyles.container}>
        <BrandHeader />
        <div style={pageStyles.card}>
          <h1 style={pageStyles.h1}>{meetingTitle || 'Meeting Summary'}</h1>
          {metaParts.length > 0 && <div style={pageStyles.metaRow}>{metaParts.join(' · ')}</div>}

          {summary && (
            <>
              <div style={pageStyles.sectionTitle}>Summary</div>
              <div style={pageStyles.summary}>{summary}</div>
            </>
          )}

          {Array.isArray(keyPoints) && keyPoints.length > 0 && (
            <>
              <div style={pageStyles.sectionTitle}>Key points</div>
              <ul style={pageStyles.nextStepsList}>
                {keyPoints.map((point, i) => (
                  <li key={i} style={pageStyles.nextStepItem}>{point}</li>
                ))}
              </ul>
            </>
          )}

          {Array.isArray(nextSteps) && nextSteps.length > 0 && (
            <>
              <div style={pageStyles.sectionTitle}>Next steps</div>
              <ul style={pageStyles.nextStepsList}>
                {nextSteps.slice(0, 8).map((step, i) => (
                  <li key={i} style={pageStyles.nextStepItem}>
                    {typeof step === 'string' ? step : (step.action || step.text || JSON.stringify(step))}
                  </li>
                ))}
              </ul>
            </>
          )}

          <div style={pageStyles.ctaWrap}>
            <div style={pageStyles.ctaLead}>Want recaps like this for your own meetings?</div>
            <a style={pageStyles.ctaButton} href='https://askmultiply.com' target='_blank' rel='noreferrer'>
              Get your own Murphy
            </a>
          </div>
        </div>
        <div style={pageStyles.footer}>
          {expiresLabel && (
            <div style={{ marginBottom: 8 }}>This summary is available until {expiresLabel}.</div>
          )}
          Powered by <a href='https://askmultiply.com' style={{ color: palette.teal }}>Multiply</a> — automate your sales meeting follow-ups.
        </div>
      </div>
    </div>
  );
};

const renderState = (title, message) => (
  <div style={pageStyles.shell}>
    <div style={pageStyles.container}>
      <BrandHeader />
      <div style={pageStyles.state}>
        <h2 style={{ marginTop: 0, color: palette.teal }}>{title}</h2>
        <p style={{ color: palette.inkSoft }}>{message}</p>
        <p style={{ marginTop: 24 }}>
          <a href='https://askmultiply.com' style={{ color: palette.teal, fontWeight: 600 }}>Learn about Multiply</a>
        </p>
      </div>
    </div>
  </div>
);

export default PublicSharedReportPage;
